import axios, { AxiosInstance, isAxiosError } from 'axios';

import { API_VERSION } from '../config';
import { BASE_URL } from '../externalConfig';
import { isErrorResponseBody } from '../types/backendType';
import { LoginService } from './loginService';
import { getRegionalApiBaseUrl } from './regionalApiStorage';
import {
  durationBucket,
  httpStatusGroup,
  normalizeHttpError,
  resolveApiOperation,
  shouldRecordApiFailure,
} from '../observability/rum/apiTelemetry';
import { getBackendRegionCode } from '../observability/rum/rumConfig';
import {
  getSupportCorrelationId,
  recordApiBreadcrumb,
  recordApiFailure,
} from '../observability/rum/rumClient';

// Stamp a start time on each request so the response/error tap can compute a
// duration bucket.
declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    metadata?: { startTime: number };
  }
  // On AxiosRequestConfig, not InternalAxiosRequestConfig, because callers pass it to
  // `httpClient.get(url, config)`; the internal config extends this one, so the interceptor
  // still sees it.
  export interface AxiosRequestConfig {
    // Statuses this specific call treats as a NORMAL outcome, so the response tap does not
    // report them as `portal.api_failed`. Use it only where the caller is PROBING for
    // something whose absence is expected — never to quieten a status that is genuinely a
    // failure, which would trade noise for blindness.
    telemetryExpectedStatuses?: number[];
  }
}

/**
 * Gets the effective API base URL.
 * Uses the regional URL from Auth Router if available, otherwise falls back to default.
 */
function getEffectiveBaseUrl(): string {
  const regionalUrl = getRegionalApiBaseUrl();
  return regionalUrl || BASE_URL;
}

function methodOf(method: string | undefined): string {
  return (method || 'GET').toUpperCase();
}

function elapsedMs(startTime: number | undefined): number {
  return typeof startTime === 'number' ? performance.now() - startTime : 0;
}

class HttpClient {
  instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      // Initial baseURL - will be overridden per-request if regional URL is set
      baseURL: BASE_URL + API_VERSION,
    });
  }
}

const httpClient = new HttpClient();

httpClient.instance.interceptors.request.use(async (config) => {
  const loginService = LoginService.getInstance();
  config.headers.Authorization = await loginService.getToken();

  // Correlation: the same opaque id is on the RUM session metadata, so support
  // can pivot RUM error → backend logs/audit. Contains no PHI.
  config.headers['X-Support-Correlation-Id'] = getSupportCorrelationId();

  // Override baseURL with regional URL if available (from Auth Router)
  // This enables multi-region API routing
  const effectiveBaseUrl = getEffectiveBaseUrl();
  if (effectiveBaseUrl !== BASE_URL) {
    config.baseURL = effectiveBaseUrl + API_VERSION;
  }

  config.metadata = { startTime: performance.now() };

  return config;
});

httpClient.instance.interceptors.response.use(
  (response) => {
    // RUM breadcrumb for every successful call (no URL/payload — op name only).
    try {
      recordApiBreadcrumb({
        apiOperation: resolveApiOperation(response.config.method, response.config.url),
        method: methodOf(response.config.method),
        httpStatusGroup: httpStatusGroup(response.status),
        durationBucket: durationBucket(elapsedMs(response.config.metadata?.startTime)),
      });
    } catch {
      /* never let telemetry break a response */
    }
    return response;
  },
  (error) => {
    // RUM breadcrumb + failure event (transport-derived fields only; never the
    // backend error message/body).
    try {
      const cfg = isAxiosError(error) ? error.config : undefined;
      const cancelled = axios.isCancel(error);
      const status = isAxiosError(error) ? error.response?.status : undefined;
      const op = resolveApiOperation(cfg?.method, cfg?.url);
      const method = methodOf(cfg?.method);
      const group = httpStatusGroup(status);
      const bucket = durationBucket(elapsedMs(cfg?.metadata?.startTime));
      // The breadcrumb is kept even for an expected status: it is the trail leading up to a
      // LATER error, and it costs nothing to keep the sequence honest.
      recordApiBreadcrumb({
        apiOperation: op,
        method,
        httpStatusGroup: group,
        durationBucket: bucket,
      });
      // Skip ONLY the failure event — never short-circuit the rest of this interceptor, which
      // still has to run the backend error-message rewrite below.
      if (shouldRecordApiFailure(status, cfg?.telemetryExpectedStatuses)) {
        recordApiFailure({
          apiOperation: op,
          method,
          httpStatusGroup: group,
          normalizedErrorCode: normalizeHttpError({
            cancelled,
            code: isAxiosError(error) ? error.code : undefined,
            status,
            hasResponse: isAxiosError(error) ? Boolean(error.response) : undefined,
          }),
          durationBucket: bucket,
          isOffline: typeof navigator !== 'undefined' ? !navigator.onLine : undefined,
          cancelled,
          backendRegion: getBackendRegionCode(),
        });
      }
    } catch {
      /* never let telemetry break error handling */
    }

    if (isAxiosError(error)) {
      const errorResponseBody = error.response?.data;
      if (isErrorResponseBody(errorResponseBody)) {
        const message = `${errorResponseBody.title}: ${errorResponseBody.details}`;
        error.message = message;
      }
    }
    throw error;
  },
);

export default httpClient.instance;
